import { useCallback, useMemo, useState } from "react"
import { DepositForAccount } from "@mezo/api"
import { SupportedTokenName } from "@mezo/sdk"

import {
  initializeWithdrawalFlow,
  RootState,
  selectActiveToken,
  selectDepositAmount,
  selectDepositFromAnotherWallet,
  selectIsConnected,
  selectTokenBalance,
  selectTokenDecimals,
  selectWithdrawalById,
  setDepositAmount,
  setDepositId,
  setOpenModal,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  BITCOIN_MIN_DEPOSIT,
  FRIENDLY_DECIMALS_AMOUNT,
} from "#/shared/constants"
import {
  bigIntToFloat,
  floatToBigInt,
  formatMats,
  validateMaximumTokenAmount,
} from "#/shared/utils"

import { useEffectOnce } from "./custom"

export type DepositBoxProps = {
  id: string
  depositId: number | null
  userAmount: string
  amount: bigint
  token: SupportedTokenName
  lockStart: Date
  lockEnd: Date
  unlockTime: string | null
  dailyMats: number
  mintedAmount: bigint
  currentLockDuration: number
}

export const useDepositInput = (initialTokenAmount?: string) => {
  const dispatch = useDappDispatch()
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const isConnected = useDappSelector(selectIsConnected)

  useEffectOnce(() => {
    if (initialTokenAmount || initialTokenAmount === "")
      dispatch(setDepositAmount(initialTokenAmount))
  })

  const setDepositTokenAmountCallback = useCallback(
    (tokenAmount: string) => {
      dispatch(setDepositAmount(tokenAmount))
    },
    [dispatch],
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const balance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, activeToken),
  )

  const depositFromAnotherWallet = useDappSelector(
    selectDepositFromAnotherWallet,
  )

  const maxTokenAmount = useMemo(
    () => (!depositFromAnotherWallet ? balance : undefined),
    [depositFromAnotherWallet, balance],
  )

  const checkValidBitcoinDepositAmount = useCallback(
    (input: string) => {
      if (activeToken !== "BTC" || !input) return true

      const parsedValue = Number(input)
      if (!parsedValue) return true

      return parsedValue >= BITCOIN_MIN_DEPOSIT
    },
    [activeToken],
  )

  const additionalValidationCallback = (input: string) => {
    const noError = {
      isValid: true,
      errorMessage: "",
    }

    if (!isConnected) {
      return noError
    }

    const isValidBitcoinInput = checkValidBitcoinDepositAmount(input)

    const isLowerOrEqualBalance = validateMaximumTokenAmount(
      floatToBigInt(input, tokenDecimals),
      balance,
    )

    if (!isValidBitcoinInput) {
      return {
        isValid: false,
        errorMessage: `Minimum deposit is ${BITCOIN_MIN_DEPOSIT} BTC`,
      }
    }

    if (!depositFromAnotherWallet && !isLowerOrEqualBalance) {
      return {
        isValid: false,
        errorMessage: "Insufficient funds",
      }
    }

    return noError
  }

  return {
    depositAmount,
    tokenDecimals,
    depositFromAnotherWallet,
    maxTokenAmount,
    setDepositTokenAmountCallback,
    additionalValidationCallback,
  }
}

export const useDepositBox = ({
  id,
  depositId,
  amount,
  token,
  dailyMats,
}: {
  id: string
  depositId: number | null
  amount: bigint | string
  token: SupportedTokenName
  dailyMats: number
}) => {
  const existingWithdrawal = useDappSelector((state: RootState) =>
    selectWithdrawalById(state, depositId),
  )

  const dispatch = useDappDispatch()

  const formattedDailyMats = formatMats(dailyMats)
  const bigintAmount = typeof amount === "string" ? BigInt(amount) : amount

  const userAmount = bigIntToFloat(bigintAmount, 18, FRIENDLY_DECIMALS_AMOUNT)

  const openWithdrawalModalHandler = useCallback(() => {
    const shouldOnlyRequestRedemption =
      token === "BTC" && existingWithdrawal
        ? !existingWithdrawal?.redemptionRequested
        : false

    dispatch(
      initializeWithdrawalFlow({
        id,
        depositId,
        amount: bigintAmount,
        userAmount,
        token,
        actionType: shouldOnlyRequestRedemption ? "redemption" : "withdraw",
        dailyMats: formattedDailyMats,
      }),
    )

    dispatch(setOpenModal("withdrawal"))
  }, [
    token,
    existingWithdrawal,
    dispatch,
    id,
    depositId,
    bigintAmount,
    userAmount,
    formattedDailyMats,
  ])

  const openReturnStbtcModalHandler = useCallback(() => {
    dispatch(setDepositId(depositId))
    dispatch(setOpenModal("stbtc-manage", { allowToChangeDeposit: false }))
  }, [dispatch, depositId])

  return {
    openWithdrawalModal: openWithdrawalModalHandler,
    openReturnStbtcModal: openReturnStbtcModalHandler,
  }
}

export const useDepositCarousel = (
  deposits: DepositForAccount[],
  onChange?: (prevIndex: number, nextIndex: number) => void,
) => {
  const [currentlySelectedDeposit, setCurrentlySelectedDeposit] = useState<
    DepositForAccount | undefined
  >(deposits[0])

  const onDepositCarouselChange = (_: number, nextIndex: number) => {
    if (deposits.length === 0) {
      setCurrentlySelectedDeposit(undefined)
      return
    }

    setCurrentlySelectedDeposit(deposits[nextIndex])

    if (onChange) onChange(_, nextIndex)
  }

  return {
    currentlySelectedDeposit,
    setCurrentlySelectedDeposit,
    onDepositCarouselChange,
  }
}
